import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BlockStack,
  Button,
  ChoiceList,
  InlineStack,
  Modal,
  Tag,
  TextField,
} from "@shopify/polaris";

export const EXCLUDE_PAGE_TYPE_OPTIONS = [
  { label: "Home", value: "home" },
  { label: "Collections", value: "collections" },
  { label: "All product pages", value: "all_product_pages" },
  { label: "Blogs", value: "blogs" },
  { label: "Blog posts", value: "blog_posts" },
  { label: "Cart", value: "cart" },
  { label: "Search", value: "search" },
  { label: "404", value: "page_404" },
  { label: "Specific URLs", value: "specific_urls" },
  { label: "Pages that contain specific keywords in URL", value: "url_keywords" },
];

const EMPTY_EXCLUDED = {
  page_types: [],
  specific_urls: [],
  url_keywords: [],
};

function TagInputField({ tags, onChange, placeholder, helpText }) {
  const [draft, setDraft] = useState("");
  const fieldWrapRef = useRef(null);

  const addTag = useCallback(
    (raw) => {
      const next = String(raw ?? draft).trim();
      if (!next) return;
      const exists = (tags || []).some((t) => String(t).toLowerCase() === next.toLowerCase());
      if (!exists) onChange([...(tags || []), next]);
      setDraft("");
    },
    [draft, onChange, tags],
  );

  const removeTag = useCallback(
    (tag) => onChange((tags || []).filter((t) => t !== tag)),
    [onChange, tags],
  );

  useEffect(() => {
    const root = fieldWrapRef.current;
    if (!root) return undefined;
    const input = root.querySelector("input");
    if (!input) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopPropagation();
      addTag(input.value);
    };

    input.addEventListener("keydown", onKeyDown);
    return () => input.removeEventListener("keydown", onKeyDown);
  }, [addTag]);

  return (
    <BlockStack gap="200">
      <div ref={fieldWrapRef}>
        <TextField
          label={placeholder}
          labelHidden
          placeholder={placeholder}
          value={draft}
          onChange={setDraft}
          autoComplete="off"
          helpText={helpText}
          connectedRight={
            <Button onClick={() => addTag(draft)} disabled={!String(draft || "").trim()}>
              Add
            </Button>
          }
        />
      </div>
      {(tags || []).length ? (
        <InlineStack gap="200" wrap>
          {(tags || []).map((tag) => (
            <Tag key={tag} onRemove={() => removeTag(tag)}>
              {tag}
            </Tag>
          ))}
        </InlineStack>
      ) : null}
    </BlockStack>
  );
}

export default function ExcludePagesModal({ open, onClose, excludedPages, onSelect }) {
  const [draftTypes, setDraftTypes] = useState([]);
  const [draftUrls, setDraftUrls] = useState([]);
  const [draftKeywords, setDraftKeywords] = useState([]);

  useEffect(() => {
    if (!open) return;
    const ex = excludedPages || EMPTY_EXCLUDED;
    setDraftTypes(ex.page_types || []);
    setDraftUrls(ex.specific_urls || []);
    setDraftKeywords(ex.url_keywords || []);
  }, [excludedPages, open]);

  const handleSelect = useCallback(() => {
    const pageTypes = [...draftTypes];
    onSelect?.({
      page_types: pageTypes,
      specific_urls: pageTypes.includes("specific_urls") ? draftUrls : [],
      url_keywords: pageTypes.includes("url_keywords") ? draftKeywords : [],
    });
    onClose?.();
  }, [draftKeywords, draftTypes, draftUrls, onClose, onSelect]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exclude pages"
      size="small"
      primaryAction={{ content: "Select", onAction: handleSelect }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <ChoiceList
            title="Exclude pages"
            titleHidden
            allowMultiple
            choices={EXCLUDE_PAGE_TYPE_OPTIONS}
            selected={draftTypes}
            onChange={setDraftTypes}
          />
          {draftTypes.includes("specific_urls") ? (
            <TagInputField
              tags={draftUrls}
              onChange={setDraftUrls}
              placeholder="Enter URL path (e.g. /pages/about-us)"
              helpText="Press Enter to add each URL"
            />
          ) : null}
          {draftTypes.includes("url_keywords") ? (
            <TagInputField
              tags={draftKeywords}
              onChange={setDraftKeywords}
              placeholder="Enter keyword (e.g. gift-card)"
              helpText="Press Enter to add each keyword"
            />
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
